import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { Plus, Trash2, Edit2, X, Check, UserPlus, Upload, User, ArrowLeft } from "lucide-react";
import { BASE_URL } from "../config";
import { notify } from "../utils/toast";
import { validatePassword } from "../utils/passwordValidation";

const AddDoctors = ({ onBack }) => {
  const [users, setUsers] = useState([]);
  const [editingUserId, setEditingUserId] = useState(null);
  const [editedUser, setEditedUser] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  
  // Form state
  const [form, setForm] = useState({
    // User registration fields
    username: "",
    email: "",
    password: "",
    // Doctor details fields
    full_name: "",
    reg_no: "",
    dateofbirth: "",
    phone_no: "",
    dept_id: "",
    profile: null,
  });
  
  const [departments, setDepartments] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, []);

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const res = await axios.get(`${BASE_URL}/api/departments/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDepartments(res.data);
    } catch (err) {
      console.error("❌ Error fetching departments:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      const { getHospitalId } = await import('../utils/auth');
      // ✅ Use selectedHospitalId if available (from hospital list), otherwise use hospitalId (logged-in hospital)
      const hospitalId = localStorage.getItem("selectedHospitalId") || getHospitalId();
      
      if (!hospitalId) {
        console.error("❌ No hospital ID found");
        notify.error("Hospital ID not found. Please try again.");
        return;
      }
      
      // console.log("📍 Fetching doctors for hospital ID:", hospitalId);
      const res = await axios.get(`${BASE_URL}/api/users/hospital/${hospitalId}/`);
      setUsers(res.data);
    } catch (err) {
      console.error("❌ Error fetching users:", err);
      const errorMsg = err.response?.data?.error || "Failed to fetch doctors";
      notify.error(errorMsg);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setForm(prev => ({ ...prev, profile: file }));
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    
    // Validate password
    const validation = validatePassword(form.password);
    if (!validation.isValid) {
      setPasswordError(validation.message);
      notify.error(validation.message);
      return;
    }
    setPasswordError('');

    // Validate phone number
    if (form.phone_no.length !== 10) {
      notify.error('Phone number must be exactly 10 digits');
      return;
    }
    
    setLoading(true);

    try {
      const { getToken, getHospitalId } = await import('../utils/auth');
      const token = getToken();
      
      if (!token) {
        notify.error("Authentication required. Please login again.");
        return;
      }
      
      // ✅ Use selectedHospitalId if available (from hospital list), otherwise use hospitalId (logged-in hospital)
      const hospitalId = localStorage.getItem("selectedHospitalId") || getHospitalId();
      
      if (!hospitalId) {
        notify.error("Hospital ID not found. Please try again.");
        return;
      }
      
      console.log("📍 Creating doctor for hospital ID:", hospitalId);

      // Fetch hospital's current package
      let currentPackageId;
      try {
        const pkgRes = await axios.get(
          `${BASE_URL}/api/superadmin/${hospitalId}/package/current/`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        currentPackageId = pkgRes.data.package_id;
      } catch (pkgErr) {
        console.error("❌ Error fetching package:", pkgErr);
        notify.error("Failed to fetch hospital package. Please try again.");
        return;
      }

      // Step 1: Register user
      let newUserId;
      try {
        const userRes = await axios.post(
          `${BASE_URL}/api/register/`,
          {
            name: form.username,
            email: form.email,
            password: form.password,
            package: currentPackageId,
            created_by: "Hospital",
            hospital_id: hospitalId,
            role:2,
          },
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        console.log("✅ User registered:", userRes.data);
        newUserId = userRes.data.user_id;
        
        if (!newUserId) {
          throw new Error("User ID not returned from registration");
        }
      } catch (userErr) {
        console.error("❌ User registration error:", userErr);
        const errorMsg = userErr.response?.data?.error || "Failed to register user";
        notify.error(errorMsg);
        return;
      }

      // Step 2: Create doctor details with image
      try {
        const formData = new FormData();
        formData.append("user_id", newUserId);
        formData.append("full_name", form.full_name);
        formData.append("email", form.email);
        formData.append("reg_no", form.reg_no);
        if (form.dateofbirth) formData.append("dateofbirth", form.dateofbirth);
        formData.append("phone_no", form.phone_no);
        if (form.dept_id) formData.append("dept_id", form.dept_id);
        formData.append("Hospital_id", hospitalId);
        if (form.profile) {
          formData.append("profile", form.profile);
        }

        const detailsRes = await axios.post(
          `${BASE_URL}/api/doctor-details/create-profile/`,
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
              Authorization: `Bearer ${token}`,
            },
          }
        );

        if (!detailsRes.data.success) {
          throw new Error(detailsRes.data.error || "Failed to create doctor profile");
        }

        console.log("✅ Doctor details created");
      } catch (detailsErr) {
        console.error("❌ Doctor details error:", detailsErr);
        const errMsg = detailsErr.response?.data?.error
          || detailsErr.response?.data?.message
          || detailsErr.message
          || "User created but failed to add doctor details.";
        notify.error(errMsg);
        fetchUsers();
        return;
      }
      
      // Reset form
      setForm({
        username: "",
        email: "",
        password: "",
        full_name: "",
        reg_no: "",
        dateofbirth: "",
        phone_no: "",
        dept_id: "",
        profile: null,
      });
      setImagePreview(null);
      setShowModal(false);
      fetchUsers();
      notify.success("Doctor registered successfully");
    } catch (err) {
      console.error("❌ Unexpected error:", err);
      notify.error("An unexpected error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user) => {
    setEditingUserId(user.user_ID);
    setEditedUser({
      name: user.name,
      email: user.email
    });
  };

  const handleEditChange = (field, value) => {
    setEditedUser(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveUpdate = async (userId) => {
    try {
      const token = localStorage.getItem("accessToken");
      await axios.put(
        `${BASE_URL}/api/users/${userId}/`,
        editedUser,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setEditingUserId(null);
      setEditedUser({});
      fetchUsers();
      notify.success("Doctor updated successfully");
    } catch (err) {
      console.error("❌ Error updating user:", err);
      notify.error("Error updating doctor");
    }
  };

  const handleCancelEdit = () => {
    setEditingUserId(null);
    setEditedUser({});
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this doctor?")) return;

    try {
      const token = localStorage.getItem("accessToken");
      await axios.delete(`${BASE_URL}/api/users/${userId}/`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      fetchUsers();
      notify.success("Doctor deleted successfully");
    } catch (err) {
      console.error("❌ Error deleting user:", err);
      notify.error("Error deleting doctor");
    }
  };

  return (
    <div className="space-y-4">
      {/* Back Button */}
      {/* Header with Back Button and Add Button */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="flex items-center justify-between mb-4"
      >
        <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
          <UserPlus className="w-7 h-7 text-primary-400" />
          Doctors Management
        </h2>
        <div className="flex items-center gap-3">
          {onBack && (
            <button
              onClick={onBack}
              className="flex items-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg transition-colors border border-slate-300"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-medium">Back</span>
            </button>
          )}
          <button
            onClick={() => setShowModal(true)}
            className="flex items-center gap-2 px-5 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors shadow-sm"
          >
            <Plus className="w-4 h-4" />
            Add Doctor
          </button>
        </div>
      </motion.div>

      {/* Registered Doctors */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
      >
        <h3 className="text-lg font-semibold text-slate-700 mb-3">Registered Doctors</h3>
        
        <div className="overflow-x-auto bg-white rounded-lg shadow-sm border border-slate-200">
          <table className="w-full">
            <thead className="bg-white">
              <tr className="border-b border-slate-200">
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Name</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Email</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Reg No</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Department</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Created By</th>
                <th className="text-left py-3 px-4 text-sm font-semibold text-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_ID} className="border-b border-slate-200 hover:bg-slate-100 transition-colors">
                  <td className="py-3 px-4">
                    {editingUserId === u.user_ID ? (
                      <input
                        type="text"
                        value={editedUser.name}
                        onChange={(e) => handleEditChange('name', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      />
                    ) : (
                      <span className="text-slate-700">{u.name}</span>
                    )}
                  </td>
                  <td className="py-3 px-4">
                    {editingUserId === u.user_ID ? (
                      <input
                        type="email"
                        value={editedUser.email}
                        onChange={(e) => handleEditChange('email', e.target.value)}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      />
                    ) : (
                      <span className="text-slate-700">{u.email}</span>
                    )}
                  </td>
                  <td className="py-3 px-4 text-slate-700">{u.reg_no || 'N/A'}</td>
                  <td className="py-3 px-4 text-slate-700">{u.department_name || 'N/A'}</td>
                  <td className="py-3 px-4 text-slate-700">{u.created_by}</td>
                  <td className="py-3 px-4">
                    <div className="flex items-center gap-2">
                      {editingUserId === u.user_ID ? (
                        <>
                          <button
                            onClick={() => handleSaveUpdate(u.user_ID)}
                            className="p-2 text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors"
                          >
                            <Check className="w-4 h-4" />
                          </button>
                          <button
                            onClick={handleCancelEdit}
                            className="p-2 text-slate-600 hover:bg-slate-750 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </>
                      ) : (
                        <>
                          <button
                            onClick={() => handleEditClick(u)}
                            className="p-2 text-primary-400 hover:bg-primary-500/10 rounded-lg transition-colors"
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(u.user_ID)}
                            className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Add Doctor Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={() => setShowModal(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Modal Header */}
              <div className="sticky top-0 bg-gradient-to-r from-medical-500 to-medical-600 text-white p-6 rounded-t-xl">
                <div className="flex justify-between items-center">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-slate-700/20 rounded-lg flex items-center justify-center">
                      <UserPlus className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold">Add New Doctor</h3>
                      <p className="text-medical-100 text-sm">Fill in the doctor's information</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowModal(false)}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <form onSubmit={handleRegister} className="p-6 space-y-5">
                {/* User Registration Section */}
                <div className="">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 text-base">
                    <User className="w-5 h-5 text-primary-400" />
                    User Account Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Username *</label>
                      <input
                        name="username"
                        value={form.username}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Email *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Password *</label>
                      <input
                        type="password"
                        name="password"
                        value={form.password}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      />
                    </div>
                  </div>
                </div>

                {/* Doctor Details Section */}
                <div className="">
                  <h4 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 text-base">
                    <UserPlus className="w-5 h-5 text-green-600" />
                    Doctor Professional Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Full Name *</label>
                      <input
                        name="full_name"
                        value={form.full_name}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Doctor's Reg No *</label>
                      <input
                        type="number"
                        name="reg_no"
                        value={form.reg_no}
                        onChange={handleChange}
                        required
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Date of Birth</label>
                      <input
                        type="date"
                        name="dateofbirth"
                        value={form.dateofbirth}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1.5">Phone Number *</label>
                      <input
                        type="tel"
                        name="phone_no"
                        value={form.phone_no}
                        onChange={(e) => {
                          // digits only, max 10
                          const digits = e.target.value.replace(/\D/g, '').slice(0, 10);
                          setForm(prev => ({ ...prev, phone_no: digits }));
                        }}
                        maxLength={10}
                        placeholder="10-digit phone number"
                        required
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      />
                      <p className="text-xs text-slate-400 mt-1">{form.phone_no.length}/10 digits</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Department</label>
                      <select
                        name="dept_id"
                        value={form.dept_id}
                        onChange={handleChange}
                        className="w-full px-3 py-2 border border-slate-300 rounded-md focus:ring-2 focus:ring-medical-500 focus:border-transparent outline-none"
                      >
                        <option value="">Select Department</option>
                        {departments.map(dept => (
                          <option key={dept.id} value={dept.id}>{dept.dept_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">Profile Picture</label>
                      <div className="flex items-center gap-2">
                        <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-slate-300 rounded-lg hover:border-medical-500 cursor-pointer transition-colors">
                          <Upload className="w-4 h-4 text-slate-600" />
                          <span className="text-sm text-slate-600">
                            {form.profile ? form.profile.name : "Choose file"}
                          </span>
                          <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageChange}
                            className="hidden"
                          />
                        </label>
                        {imagePreview && (
                          <img src={imagePreview} alt="Preview" className="w-14 h-14 rounded-lg object-cover" />
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Submit Button */}
                <div className="flex justify-end gap-3 pt-3 border-t border-slate-200">
                  <button
                    type="button"
                    onClick={() => setShowModal(false)}
                    className="px-5 py-2 border border-slate-300 text-slate-700 font-medium rounded-lg hover:bg-slate-100 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="px-5 py-2 bg-primary-500/100 text-white font-medium rounded-lg hover:bg-medical-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    <UserPlus className="w-5 h-5" />
                    {loading ? "Creating..." : "Create Doctor"}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AddDoctors;



